"use client";

import Link from "next/link";
import type { ComponentProps } from "react";

import { usePricingDestination } from "@/hooks/use-pricing-destination";
import { cn } from "@/lib/utils";

type PricingLinkProps = Omit<ComponentProps<typeof Link>, "href"> & {
  children: React.ReactNode;
};

/**
 * Auth-aware pricing link — guests → `/pricing`, signed-in → `/billing/pricing`.
 * Renders a non-navigating current-page label when already on the destination.
 */
export function PricingLink({
  children,
  className,
  onClick,
  ...props
}: PricingLinkProps) {
  const { href, isCurrent, suppressNavigation } = usePricingDestination();

  if (isCurrent && suppressNavigation) {
    return (
      <span
        className={cn(className)}
        aria-current="page"
        data-pricing-current
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      href={href}
      className={className}
      onClick={onClick}
      {...props}
    >
      {children}
    </Link>
  );
}
